import { useState, useEffect } from 'react';
import { scanSourcesApi, documentTypesApi, settingsApi, usersApi, rgpdApi } from '../api';
import type { AppSettings, User, UserCreate, ScanSource, ScanSourceCreate, DocumentType } from '../types';
import {
  RefreshCw, CheckCircle, XCircle, Trash2, Pencil,
  SlidersHorizontal, FileText, Cpu, Bell, Shield, Users, Plug, Save,
  Mail, Clock, AlertCircle, UserPlus, Crown, Lock,
  Plus, X,
} from 'lucide-react';
import { Header, toast } from '../components/ui';
import { useSettings } from '../context/SettingsContext';

// ── Intégrations ─────────────────────────────────────────────────

function IntegrationsTab() {
  const { formatDate } = useSettings();
  const [sources, setSources] = useState<ScanSource[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [triggering, setTriggering] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<ScanSourceCreate>({ name: '', source_type: 'directory', path: '', config: { recursive: false }, schedule_cron: '0 */2 * * *', is_active: true });
  const [form, setForm] = useState<ScanSourceCreate>({
    name: '',
    source_type: 'directory',
    path: '',
    config: { recursive: false },
    schedule_cron: '0 */2 * * *',
    is_active: true,
  });

  const load = () => {
    setLoading(true);
    scanSourcesApi.list()
      .then(r => setSources(r.data))
      .catch(() => setSources([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.path?.trim()) return;
    await scanSourcesApi.create(form);
    setShowForm(false);
    setForm({ name: '', source_type: 'directory', path: '', config: { recursive: false }, schedule_cron: '0 */2 * * *', is_active: true });
    load();
  };

  const handleEdit = (s: ScanSource) => {
    setEditingId(s.id);
    setEditForm({ name: s.name, source_type: s.source_type, path: s.path ?? '', config: s.config ?? { recursive: false }, schedule_cron: s.schedule_cron ?? '0 */2 * * *', is_active: s.is_active });
    setShowForm(false);
  };

  const handleSave = async () => {
    if (!editingId || !editForm.name.trim() || !editForm.path?.trim()) return;
    await scanSourcesApi.update(editingId, editForm);
    setEditingId(null);
    load();
  };

  const handleToggle = async (s: ScanSource) => {
    await scanSourcesApi.update(s.id, { is_active: !s.is_active });
    load();
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Supprimer cette source ?')) return;
    await scanSourcesApi.delete(id);
    load();
  };

  const handleTrigger = async (s: ScanSource) => {
    setTriggering(s.id);
    try { await scanSourcesApi.trigger(s.id); }
    finally { setTriggering(null); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Plug size={20} className="text-gray-400" strokeWidth={1.5} />
            <h3 className="font-bold text-gray-900 text-lg">Sources de scan</h3>
          </div>
          <p className="text-sm text-gray-500 mt-1">Répertoires locaux surveillés automatiquement pour importer de nouveaux documents.</p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition"
        >
          + Ajouter une source
        </button>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
        <span className="text-blue-500 text-lg mt-0.5">ℹ️</span>
        <div className="text-sm text-blue-800">
          <p className="font-semibold">Comment configurer l'accès aux fichiers</p>
          <p className="mt-1 text-blue-700">
            Saisissez le <span className="font-semibold">chemin réel</span> sur votre poste.
          </p>
        </div>
      </div>

      {showForm && (
        <div className="bg-white border border-teal-200 rounded-xl p-5 shadow-sm space-y-4">
          <h4 className="font-bold text-gray-900">Nouvelle source de scan</h4>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom</label>
            <input
              type="text"
              placeholder="ex : Documents RH"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">
              Chemin du répertoire <span className="font-normal text-gray-400">(chemin réel sur votre poste)</span>
            </label>
            <input
              type="text"
              placeholder="ex : C:\Users\moi\Documents\RH  ou  /home/moi/documents/RH"
              value={form.path || ''}
              onChange={e => setForm(f => ({ ...f, path: e.target.value }))}
              className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-mono focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="recursive-check"
              checked={form.config?.recursive || false}
              onChange={e => setForm(f => ({ ...f, config: { ...f.config, recursive: e.target.checked } }))}
              className="w-4 h-4 accent-teal-600"
            />
            <label htmlFor="recursive-check" className="text-sm font-medium text-gray-700 cursor-pointer">
              Scanner les sous-dossiers (récursif)
            </label>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Fréquence (cron)</label>
            <div className="flex flex-wrap gap-2">
              {[
                { label: 'Toutes les heures', value: '0 * * * *' },
                { label: 'Toutes les 2h', value: '0 */2 * * *' },
                { label: '2×/jour', value: '0 8,18 * * *' },
                { label: 'Chaque jour 8h', value: '0 8 * * *' },
              ].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setForm(f => ({ ...f, schedule_cron: opt.value }))}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${form.schedule_cron === opt.value ? 'bg-teal-50 border-teal-400 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button onClick={() => setShowForm(false)} className="border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
              Annuler
            </button>
            <button
              onClick={handleCreate}
              disabled={!form.name.trim() || !form.path?.trim()}
              className="bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
            >
              Créer la source
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-400">
          <RefreshCw className="w-8 h-8 mx-auto mb-2 animate-spin" />
          <p className="text-sm">Chargement des sources...</p>
        </div>
      ) : sources.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-dashed border-gray-200">
          <span className="text-4xl">📁</span>
          <p className="text-gray-600 font-semibold mt-3">Aucune source configurée</p>
          <p className="text-sm text-gray-400 mt-1">Ajoutez un répertoire local pour importer automatiquement des documents.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sources.map(s => (
            <div key={s.id} className="bg-white border border-gray-100 rounded-xl shadow-sm overflow-hidden">
              {editingId === s.id ? (
                /* ── Mode édition ── */
                <div className="p-5 space-y-4 border-l-4 border-teal-400">
                  <p className="text-sm font-bold text-gray-900">Modifier la source</p>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Chemin du répertoire <span className="font-normal text-gray-400">(chemin réel sur votre poste)</span>
                    </label>
                    <input
                      type="text"
                      value={editForm.path || ''}
                      onChange={e => setEditForm(f => ({ ...f, path: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm font-mono focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`recursive-edit-${s.id}`}
                      checked={editForm.config?.recursive || false}
                      onChange={e => setEditForm(f => ({ ...f, config: { ...f.config, recursive: e.target.checked } }))}
                      className="w-4 h-4 accent-teal-600"
                    />
                    <label htmlFor={`recursive-edit-${s.id}`} className="text-sm font-medium text-gray-700 cursor-pointer">
                      Scanner les sous-dossiers (récursif)
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1.5">Fréquence (cron)</label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: 'Toutes les heures', value: '0 * * * *' },
                        { label: 'Toutes les 2h', value: '0 */2 * * *' },
                        { label: '2×/jour', value: '0 8,18 * * *' },
                        { label: 'Chaque jour 8h', value: '0 8 * * *' },
                      ].map(opt => (
                        <button
                          key={opt.value}
                          onClick={() => setEditForm(f => ({ ...f, schedule_cron: opt.value }))}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition ${editForm.schedule_cron === opt.value ? 'bg-teal-50 border-teal-400 text-teal-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                    <button onClick={() => setEditingId(null)} className="border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">
                      Annuler
                    </button>
                    <button
                      onClick={handleSave}
                      disabled={!editForm.name.trim() || !editForm.path?.trim()}
                      className="bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition"
                    >
                      <Save className="w-4 h-4" /> Enregistrer
                    </button>
                  </div>
                </div>
              ) : (
                /* ── Mode affichage ── */
                <div className="flex items-center justify-between p-5 gap-4">
                  <div className="flex items-center gap-4 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 bg-gray-100">
                      📁
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-bold text-gray-900 text-sm">{s.name}</p>
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${s.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                          {s.is_active ? 'Actif' : 'Inactif'}
                        </span>
                        {s.config?.recursive && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-500">récursif</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 font-mono truncate">
                        {s.path || '—'}
                        {s.schedule_cron && <span className="font-sans ml-2 text-gray-400">• {s.schedule_cron}</span>}
                      </p>
                      {s.last_scan_at && (
                        <p className="text-xs text-gray-400 mt-0.5">
                          Dernier scan : {formatDate(s.last_scan_at, { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button onClick={() => handleTrigger(s)} disabled={!s.is_active || triggering === s.id} title="Lancer le scan" className="p-2 hover:bg-teal-50 text-teal-600 rounded-lg disabled:opacity-40 transition">
                      <RefreshCw className={`w-4 h-4 ${triggering === s.id ? 'animate-spin' : ''}`} />
                    </button>
                    <button onClick={() => handleToggle(s)} title={s.is_active ? 'Désactiver' : 'Activer'} className="p-2 hover:bg-gray-100 rounded-lg transition">
                      {s.is_active ? <CheckCircle className="w-4 h-4 text-emerald-500" /> : <XCircle className="w-4 h-4 text-gray-400" />}
                    </button>
                    <button onClick={() => handleEdit(s)} title="Modifier" className="p-2 hover:bg-gray-100 text-gray-500 hover:text-gray-700 rounded-lg transition">
                      <Pencil className="w-4 h-4" />
                    </button>
                    <button onClick={() => handleDelete(s.id)} title="Supprimer" className="p-2 hover:bg-red-50 text-red-400 hover:text-red-600 rounded-lg transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Général ───────────────────────────────────────────────────────

type SettingsDraft = Omit<AppSettings, 'updated_at'>;

function GeneralTab({ draft, onChange }: { draft: SettingsDraft; onChange: (p: Partial<SettingsDraft>) => void }) {
  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2">
          <SlidersHorizontal size={20} className="text-gray-400" strokeWidth={1.5} />
          <h3 className="font-bold text-gray-900 text-lg">Paramètres généraux</h3>
        </div>
        <p className="text-sm text-gray-500 mt-1">Configuration générale de l'application ACO DOC.</p>
      </div>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Nom de l'organisation</label>
          <input
            type="text"
            value={draft.organisation_name}
            onChange={e => onChange({ organisation_name: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Langue par défaut</label>
          <select
            value={draft.default_language}
            onChange={e => onChange({ default_language: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm bg-white"
          >
            <option value="fr">Français</option>
            <option value="de">Deutsch</option>
            <option value="en">English</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Fuseau horaire</label>
          <select
            value={draft.timezone}
            onChange={e => onChange({ timezone: e.target.value })}
            className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm bg-white"
          >
            <option value="Europe/Luxembourg">Europe/Luxembourg (UTC+1)</option>
            <option value="Europe/Paris">Europe/Paris (UTC+1)</option>
            <option value="Europe/Brussels">Europe/Brussels (UTC+1)</option>
            <option value="UTC">UTC</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Seuil de confiance classification</label>
          <div className="flex items-center gap-3">
            <input
              type="range" min="50" max="99"
              value={draft.classification_threshold}
              onChange={e => onChange({ classification_threshold: Number(e.target.value) })}
              className="flex-1 accent-teal-600"
            />
            <input
              type="number" min="50" max="99"
              value={draft.classification_threshold}
              onChange={e => {
                const v = Math.min(99, Math.max(50, Number(e.target.value)));
                if (!isNaN(v)) onChange({ classification_threshold: v });
              }}
              className="w-16 text-sm font-bold text-gray-700 border border-gray-200 rounded-lg px-2 py-1 text-center focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
            />
            <span className="text-sm text-gray-400">%</span>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Seuil de qualité OCR</label>
          <div className="flex items-center gap-3">
            <input
              type="range" min="50" max="99"
              value={draft.ocr_threshold}
              onChange={e => onChange({ ocr_threshold: Number(e.target.value) })}
              className="flex-1 accent-teal-600"
            />
            <input
              type="number" min="50" max="99"
              value={draft.ocr_threshold}
              onChange={e => {
                const v = Math.min(99, Math.max(50, Number(e.target.value)));
                if (!isNaN(v)) onChange({ ocr_threshold: v });
              }}
              className="w-16 text-sm font-bold text-gray-700 border border-gray-200 rounded-lg px-2 py-1 text-center focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
            />
            <span className="text-sm text-gray-400">%</span>
          </div>
          <p className="text-xs text-gray-500 mt-1.5">
            En dessous de ce seuil, le document est automatiquement marqué « À vérifier » pour contrôle manuel.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Types de documents ────────────────────────────────────────────

const RETENTION_OPTIONS = [
  { value: 0,    label: 'Illimitée' },
  { value: 365,  label: '1 an' },
  { value: 730,  label: '2 ans' },
  { value: 1095, label: '3 ans' },
  { value: 1825, label: '5 ans' },
  { value: 2555, label: '7 ans' },
  { value: 3650, label: '10 ans' },
  { value: 5475, label: '15 ans' },
  { value: 7300, label: '20 ans' },
];

const COLOR_PRESETS = [
  '#14b8a6', '#6366f1', '#f59e0b', '#ef4444',
  '#3b82f6', '#8b5cf6', '#ec4899', '#10b981',
  '#f97316', '#6b7280',
];

const EMPTY_FORM = {
  label: '',
  code: '',
  description: '',
  retention_days: 1825,
  retention_rule: '',
  color: '#14b8a6',
  fields: [] as string[],
  fieldInput: '',
};

function TypesTab() {
  const [types, setTypes]       = useState<DocumentType[]>([]);
  const [loading, setLoading]   = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [saving, setSaving]     = useState<string | null>(null);
  // Edition rétention inline : { [id]: { days, rule } }
  const [edits, setEdits]       = useState<Record<string, { days: string; rule: string }>>({});
  // Codes de types ayant des alertes RGPD actives
  const [alertTypeCodes, setAlertTypeCodes] = useState<Set<string>>(new Set());

  // ── Création ──────────────────────────────────────────────────────
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating]     = useState(false);
  const [form, setForm]             = useState({ ...EMPTY_FORM });

  const slugify = (s: string) =>
    s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')
      .replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '').slice(0, 50);

  const handleLabelChange = (label: string) => {
    setForm(f => ({ ...f, label, code: slugify(label) }));
  };

  const handleAddField = () => {
    const v = form.fieldInput.trim();
    if (!v || form.fields.includes(v)) return;
    setForm(f => ({ ...f, fields: [...f.fields, v], fieldInput: '' }));
  };

  const handleRemoveField = (field: string) => {
    setForm(f => ({ ...f, fields: f.fields.filter(x => x !== field) }));
  };

  const handleCreate = async () => {
    if (!form.label.trim() || !form.code.trim()) return;
    setCreating(true);
    try {
      await documentTypesApi.create({
        label: form.label.trim(),
        code: form.code.trim(),
        description: form.description.trim() || undefined,
        retention_days: form.retention_days,
        retention_rule: form.retention_rule.trim() || undefined,
        color: form.color,
        extraction_schema: form.fields.length > 0 ? { fields: form.fields } : undefined,
      });
      toast('Type de document créé avec succès');
      setShowCreate(false);
      setForm({ ...EMPTY_FORM });
      load();
    } catch {
      toast('Erreur lors de la création', 'error');
    } finally {
      setCreating(false);
    }
  };

  const load = () => {
    setLoading(true);
    documentTypesApi.list()
      .then(r => setTypes(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // Charge les alertes RGPD actives pour détecter les types "Illimitée" incohérents
    rgpdApi.listAlerts({ status: 'active', size: 500 })
      .then(r => {
        const codes = new Set<string>();
        r.data.items.forEach(a => {
          const code = a.document?.document_type?.code;
          if (code) codes.add(code);
        });
        setAlertTypeCodes(codes);
      })
      .catch(() => {});
  }, []);

  const handleSaveRetention = async (t: DocumentType) => {
    const e = edits[t.id];
    if (!e) return;
    const days = e.days === '' ? undefined : Number(e.days);
    const rule = e.rule.trim();
    // Garde-fou : si une durée finie est saisie, on exige une règle non vide et
    // non incohérente avec le placeholder "illimitée" laissé par l'auto-création IA.
    if (days != null && days > 0) {
      if (!rule) {
        toast('Merci de préciser la règle RGPD correspondant à la durée saisie.', 'error');
        return;
      }
      if (/illimit/i.test(rule)) {
        toast('La règle RGPD ne peut pas mentionner « illimité » si une durée est fixée.', 'error');
        return;
      }
    }
    setSaving(t.id);
    try {
      await documentTypesApi.update(t.id, {
        retention_days: days,
        retention_rule: rule || undefined,
      });
      setEdits(prev => { const n = { ...prev }; delete n[t.id]; return n; });
      load();
      window.dispatchEvent(new Event('acodoc:refresh-sidebar'));
    } finally {
      setSaving(null);
    }
  };

  const retentionLabel = (days?: number) => {
    if (days == null || days === 0) return 'Illimitée';
    if (days >= 365) return `${Math.round(days / 365)} an${Math.round(days / 365) > 1 ? 's' : ''}`;
    return `${days} jours`;
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <RefreshCw className="w-6 h-6 text-gray-300 animate-spin" />
      </div>
    );
  }

  // Types auto-créés qui n'ont PAS encore de rétention configurée (toujours à 0/illimitée)
  const autoCreatedTypes = types.filter(t => t.auto_created && (!t.retention_days || t.retention_days === 0));

  return (
    <div className="space-y-5">
      {/* En-tête */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-2">
          <FileText size={20} className="text-gray-400" strokeWidth={1.5} />
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Types de documents</h3>
            <p className="text-sm text-gray-500 mt-0.5">
              {types.length} type{types.length > 1 ? 's' : ''} configuré{types.length > 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => {
            const nextColor = COLOR_PRESETS[types.length % COLOR_PRESETS.length];
            setForm({ ...EMPTY_FORM, color: nextColor });
            setShowCreate(true);
          }}
          className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-3 py-2 rounded-lg text-sm font-semibold transition shrink-0"
        >
          <Plus size={15} strokeWidth={2.5} />
          Nouveau type
        </button>
      </div>

      {/* Bandeau d'alerte : types auto-créés par l'IA avec durée illimitée */}
      {autoCreatedTypes.length > 0 && (
        <div className="flex items-start gap-3 border border-amber-200 bg-amber-50 rounded-xl px-4 py-3">
          <AlertCircle size={18} className="text-amber-600 shrink-0 mt-0.5" strokeWidth={2} />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              {autoCreatedTypes.length} type{autoCreatedTypes.length > 1 ? 's' : ''} créé{autoCreatedTypes.length > 1 ? 's' : ''} automatiquement par l'IA
            </p>
            <p className="text-xs text-amber-800 mt-0.5">
              Leur durée de conservation est par défaut <strong>illimitée</strong>. Merci de configurer la rétention RGPD&nbsp;:{' '}
              {autoCreatedTypes.map((t, i) => (
                <span key={t.id}>
                  <button
                    onClick={() => setExpanded(t.id)}
                    className="font-semibold underline hover:text-amber-900"
                  >
                    {t.label}
                  </button>
                  {i < autoCreatedTypes.length - 1 ? ', ' : ''}
                </span>
              ))}
              .
            </p>
          </div>
        </div>
      )}

      {/* ── Modale création ──────────────────────────────────────── */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
            {/* Header modale */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-bold text-gray-900 text-base">Nouveau type de document</h2>
              <button onClick={() => { setShowCreate(false); setForm({ ...EMPTY_FORM }); }} className="text-gray-400 hover:text-gray-600">
                <X size={18} />
              </button>
            </div>

            {/* Corps */}
            <div className="px-6 py-5 space-y-4 max-h-[70vh] overflow-y-auto">

              {/* Nom */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Nom du type <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Ex : Carte nationale d'identité"
                  value={form.label}
                  onChange={e => handleLabelChange(e.target.value)}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
                />
              </div>

              {/* Code */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Code identifiant <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Ex : cni"
                  value={form.code}
                  onChange={e => setForm(f => ({ ...f, code: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm font-mono focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
                />
                <p className="text-xs text-gray-400 mt-1">Identifiant unique utilisé par le pipeline IA pour la classification.</p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Description</label>
                <textarea
                  rows={2}
                  placeholder="Description optionnelle du type de document…"
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
                />
              </div>

              {/* Durée de conservation */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Durée de conservation RGPD
                </label>
                <select
                  value={form.retention_days}
                  onChange={e => setForm(f => ({ ...f, retention_days: Number(e.target.value) }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
                >
                  {RETENTION_OPTIONS.map(o => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Ex : 5 ans après la fin du contrat"
                  value={form.retention_rule}
                  onChange={e => setForm(f => ({ ...f, retention_rule: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mt-2 focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
                />
                <p className="text-xs text-gray-400 mt-1">La règle textuelle apparaîtra dans la section RGPD.</p>
              </div>

              {/* Entités à extraire */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                  Entités à extraire (pipeline IA)
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Ex : date_naissance, nationalite…"
                    value={form.fieldInput}
                    onChange={e => setForm(f => ({ ...f, fieldInput: e.target.value }))}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddField(); } }}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
                  />
                  <button
                    type="button"
                    onClick={handleAddField}
                    className="px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-gray-600 transition"
                  >
                    Ajouter
                  </button>
                </div>
                {form.fields.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {form.fields.map(field => (
                      <span key={field} className="flex items-center gap-1 bg-teal-50 border border-teal-200 text-teal-700 text-xs font-mono px-2 py-1 rounded-full">
                        {field}
                        <button onClick={() => handleRemoveField(field)} className="text-teal-400 hover:text-teal-700">
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-400 mt-1">Ces champs seront recherchés lors du traitement IA du document.</p>
              </div>

              {/* Couleur */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">Couleur</label>
                <div className="flex items-center gap-2 flex-wrap">
                  {COLOR_PRESETS.map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm(f => ({ ...f, color: c }))}
                      className={`w-6 h-6 rounded-full border-2 transition ${form.color === c ? 'border-gray-800 scale-110' : 'border-transparent'}`}
                      style={{ background: c }}
                    />
                  ))}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-gray-100 bg-gray-50">
              <button
                onClick={() => { setShowCreate(false); setForm({ ...EMPTY_FORM }); }}
                className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleCreate}
                disabled={creating || !form.label.trim() || !form.code.trim()}
                className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
              >
                {creating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle size={15} />}
                Créer le type
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Liste des types */}
      <div className="space-y-2">
        {types.map(t => {
          const isExpanded = expanded === t.id;
          const isSaving   = saving === t.id;
          const edit       = edits[t.id];
          const dotColor   = t.color ?? '#6b7280';

          return (
            <div key={t.id} className={`border rounded-xl overflow-hidden transition-all ${t.is_active ? 'border-gray-100' : 'border-gray-100 opacity-60'}`}>
              {/* Ligne principale */}
              <div className="flex items-center justify-between px-4 py-3 bg-white gap-3">
                <button
                  onClick={() => setExpanded(isExpanded ? null : t.id)}
                  className="flex items-center gap-3 flex-1 text-left min-w-0"
                >
                  {/* Pastille couleur */}
                  <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: dotColor }} />
                  <span className="text-sm font-semibold text-gray-800 truncate">{t.label}</span>
                  {/* Badge "À configurer" : seulement si auto-créé ET rétention encore illimitée */}
                  {t.auto_created && (!t.retention_days || t.retention_days === 0) && (
                    <span
                      className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded-full shrink-0"
                      title="Type créé automatiquement par l'IA — durée de conservation à configurer"
                    >
                      <AlertCircle size={11} strokeWidth={2.5} />
                      À configurer
                    </span>
                  )}
                  {/* Badge rouge : rétention illimitée mais alertes RGPD actives → incohérence */}
                  {(!t.retention_days || t.retention_days === 0) && alertTypeCodes.has(t.code) && (
                    <span
                      className="flex items-center gap-1 text-xs font-semibold text-red-700 bg-red-50 border border-red-200 px-1.5 py-0.5 rounded-full shrink-0"
                      title="Ce type est en « Illimitée » mais a des alertes RGPD actives — vérifiez la rétention"
                    >
                      <AlertCircle size={11} strokeWidth={2.5} />
                      Alertes actives
                    </span>
                  )}
                  {t.retention_days != null && (
                    <span className="text-xs text-gray-400 shrink-0 hidden sm:inline">
                      {retentionLabel(t.retention_days)}
                    </span>
                  )}
                </button>

              </div>

              {/* Panneau déplié */}
              {isExpanded && (
                <div className="px-4 pb-4 bg-gray-50 border-t border-gray-100 space-y-4">

                  {/* Description */}
                  {t.description && (
                    <p className="text-xs text-gray-500 mt-3">{t.description}</p>
                  )}

                  {/* Avertissement type auto-créé par l'IA (seulement si rétention encore illimitée) */}
                  {t.auto_created && (!t.retention_days || t.retention_days === 0) && (
                    <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded-lg px-3 py-2 mt-3">
                      <AlertCircle size={14} className="text-amber-600 shrink-0 mt-0.5" strokeWidth={2} />
                      <p className="text-xs text-amber-800">
                        Ce type a été créé automatiquement lors de la classification IA. Sa durée de conservation est par défaut <strong>illimitée</strong>&nbsp;— merci de la configurer ci-dessous pour respecter la RGPD.
                      </p>
                    </div>
                  )}
                  {/* Avertissement incohérence : rétention illimitée mais alertes RGPD actives */}
                  {(!t.retention_days || t.retention_days === 0) && alertTypeCodes.has(t.code) && (
                    <div className="flex items-start gap-2 border border-red-200 bg-red-50 rounded-lg px-3 py-2 mt-3">
                      <AlertCircle size={14} className="text-red-600 shrink-0 mt-0.5" strokeWidth={2} />
                      <p className="text-xs text-red-800">
                        Ce type est configuré en <strong>conservation illimitée</strong>, mais des <strong>alertes RGPD actives</strong> existent pour ses documents. Vérifiez la durée de rétention ci-dessous.
                      </p>
                    </div>
                  )}

                  {/* Rétention RGPD — éditable */}
                  <div>
                    <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                      Rétention RGPD
                    </p>
                    {edit ? (
                      <div className="space-y-2">
                        <div className="flex gap-2 items-center">
                          <input
                            type="number"
                            min="0"
                            placeholder="Jours (0 = illimité)"
                            value={edit.days}
                            onChange={e => setEdits(prev => {
                              const cur = prev[t.id];
                              const nextDays = e.target.value;
                              // Si on passe à une durée finie et que la règle mentionne encore
                               // "illimité", on la vide pour forcer une saisie cohérente.
                              const nextRule = (Number(nextDays) > 0 && /illimit/i.test(cur.rule))
                                ? ''
                                : cur.rule;
                              return { ...prev, [t.id]: { days: nextDays, rule: nextRule } };
                            })}
                            className="w-40 border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                          />
                          <span className="text-xs text-gray-400">jours</span>
                        </div>
                        <input
                          type="text"
                          placeholder="Ex : 5 ans après départ du collaborateur"
                          value={edit.rule}
                          onChange={e => setEdits(prev => ({ ...prev, [t.id]: { ...prev[t.id], rule: e.target.value } }))}
                          className="w-full border border-gray-200 rounded-lg px-3 py-1.5 text-sm bg-white"
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveRetention(t)}
                            disabled={isSaving}
                            className="flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition"
                          >
                            {isSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                            Enregistrer
                          </button>
                          <button
                            onClick={() => setEdits(prev => { const n = { ...prev }; delete n[t.id]; return n; })}
                            className="px-3 py-1.5 rounded-lg text-xs font-semibold text-gray-500 hover:bg-gray-100 transition"
                          >
                            Annuler
                          </button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="flex-1">
                          <p className="text-sm font-semibold text-gray-800">
                            {retentionLabel(t.retention_days)}
                          </p>
                          {t.retention_rule && (
                            <p className="text-xs text-gray-400 mt-0.5">{t.retention_rule}</p>
                          )}
                        </div>
                        <button
                          onClick={() => setEdits(prev => ({
                            ...prev,
                            [t.id]: {
                              days: t.retention_days != null ? String(t.retention_days) : '',
                              rule: t.retention_rule ?? '',
                            },
                          }))}
                          className="text-xs text-teal-600 hover:text-teal-800 font-medium shrink-0"
                        >
                          Modifier
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Notifications ─────────────────────────────────────────────────

type NotifKey = 'new_doc' | 'rgpd_expiry' | 'scan_error' | 'weekly_report' | 'low_confidence';

const NOTIF_ITEMS: { key: NotifKey; label: string; desc: string; icon: typeof Bell }[] = [
  { key: 'new_doc',        label: 'Nouveau document importé',    desc: 'À chaque import automatique ou manuel réussi.',         icon: FileText    },
  { key: 'rgpd_expiry',    label: 'Document expirant bientôt',   desc: 'Alerte 30 jours avant expiration d\'un document RGPD.', icon: Clock       },
  { key: 'scan_error',     label: 'Erreur de scan',              desc: 'Quand une source de scan échoue ou est inaccessible.',  icon: AlertCircle },
  { key: 'low_confidence', label: 'Classification incertaine',   desc: 'Document classifié sous le seuil de confiance.',       icon: Cpu         },
  { key: 'weekly_report',  label: 'Rapport hebdomadaire',        desc: 'Synthèse par email chaque lundi matin.',                icon: Mail        },
];

function NotificationsTab({ draft, onChange }: { draft: SettingsDraft; onChange: (p: Partial<SettingsDraft>) => void }) {
  const notifField: Record<NotifKey, keyof SettingsDraft> = {
    new_doc:        'notify_new_doc',
    rgpd_expiry:    'notify_rgpd_expiry',
    scan_error:     'notify_scan_error',
    low_confidence: 'notify_low_confidence',
    weekly_report:  'notify_weekly_report',
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Bell size={20} className="text-gray-400" strokeWidth={1.5} />
        <div>
          <h3 className="font-bold text-gray-900 text-lg">Notifications</h3>
          <p className="text-sm text-gray-500 mt-0.5">Configurez les alertes email et notifications système.</p>
        </div>
      </div>

      <div className="border border-gray-100 rounded-xl p-4">
        <label className="block text-sm font-semibold text-gray-700 mb-2">Adresse email de notification</label>
        <div className="relative">
          <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" strokeWidth={1.8} />
          <input
            type="email"
            value={draft.notification_email ?? ''}
            onChange={e => onChange({ notification_email: e.target.value })}
            className="w-full border border-gray-200 rounded-lg pl-9 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
          />
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">Événements</p>
        {NOTIF_ITEMS.map(({ key, label, desc, icon: Icon }) => (
          <div key={key} className="flex items-center justify-between px-4 py-3.5 border border-gray-100 rounded-xl bg-white">
            <div className="flex items-start gap-3">
              <Icon size={16} className="text-gray-400 mt-0.5 shrink-0" strokeWidth={1.6} />
              <div>
                <p className="text-sm font-semibold text-gray-800">{label}</p>
                <p className="text-xs text-gray-400 mt-0.5">{desc}</p>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={draft[notifField[key]] as boolean}
                onChange={() => onChange({ [notifField[key]]: !draft[notifField[key]] })}
              />
              <div className="w-9 h-5 bg-gray-200 peer-checked:bg-teal-500 rounded-full transition after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── RGPD & Conservation ───────────────────────────────────────────


const DUREE_OPTIONS = [
  { value: 0,    label: 'Illimitée' },
  { value: 365,  label: '1 an' },
  { value: 730,  label: '2 ans' },
  { value: 1095, label: '3 ans' },
  { value: 1825, label: '5 ans' },
  { value: 2555, label: '7 ans' },
  { value: 3650, label: '10 ans' },
  { value: 5475, label: '15 ans' },
  { value: 7300, label: '20 ans' },
];

function RgpdTab({ draft, onChange }: { draft: SettingsDraft; onChange: (p: Partial<SettingsDraft>) => void }) {
  const [docTypes, setDocTypes]   = useState<DocumentType[]>([]);
  // localDays : valeur en cours d'édition par type { [id]: days }
  const [localDays, setLocalDays] = useState<Record<string, number>>({});
  const [saving, setSaving]       = useState<string | null>(null);
  const [saved, setSaved]         = useState<string | null>(null);

  useEffect(() => {
    documentTypesApi.list().then(r => {
      setDocTypes(r.data);
      const init: Record<string, number> = {};
      r.data.forEach(t => { init[t.id] = t.retention_days ?? 0; });
      setLocalDays(init);
    }).catch(() => {});
  }, []);

  const isDirty = (t: DocumentType) => localDays[t.id] !== (t.retention_days ?? 0);

  const handleSave = async (t: DocumentType) => {
    setSaving(t.id);
    try {
      await documentTypesApi.update(t.id, { retention_days: localDays[t.id] });
      setDocTypes(ts => ts.map(x => x.id === t.id ? { ...x, retention_days: localDays[t.id] } : x));
      setSaved(t.id);
      setTimeout(() => setSaved(null), 2000);
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      console.error('[handleSave retention]', status, detail, err);
      toast(detail ? `Erreur ${status} : ${detail}` : 'Erreur lors de la mise à jour', 'error');
    } finally {
      setSaving(null);
    }
  };

  const handleCancel = (t: DocumentType) => {
    setLocalDays(prev => ({ ...prev, [t.id]: t.retention_days ?? 0 }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Shield size={20} className="text-gray-400" strokeWidth={1.5} />
        <div>
          <h3 className="font-bold text-gray-900 text-lg">RGPD & Conservation</h3>
          <p className="text-sm text-gray-500 mt-0.5">Durées de conservation par type de document.</p>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3">
        <Lock size={16} className="text-amber-500 mt-0.5 shrink-0" strokeWidth={1.8} />
        <p className="text-sm text-amber-800">
          Ces durées sont conformes au RGPD et au droit luxembourgeois. Modifiez uniquement si votre DPO l'approuve.
          <span className="font-semibold"> Illimitée = aucune expiration.</span>
        </p>
      </div>

      {/* ── Durée de conservation ──────────────────────────────── */}
      <div className="space-y-2">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider px-1">Durée de conservation</p>
        {docTypes.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">Aucun type de document configuré.</p>
        )}
        {docTypes.map(t => {
          const dirty     = isDirty(t);
          const isSaving  = saving === t.id;
          const isSaved   = saved === t.id;
          return (
            <div key={t.id} className="flex items-center gap-3 px-4 py-3 border border-gray-100 rounded-xl bg-white">
              {/* Pastille couleur */}
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: t.color ?? '#6b7280' }} />

              {/* Label + règle */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-800 truncate">{t.label}</p>
              </div>

              {/* Select durée */}
              <select
                value={localDays[t.id] ?? 0}
                onChange={e => setLocalDays(prev => ({ ...prev, [t.id]: Number(e.target.value) }))}
                className={`border rounded-lg px-3 py-1.5 text-sm bg-white transition shrink-0 ${dirty ? 'border-teal-400 ring-2 ring-teal-500/20' : 'border-gray-200'}`}
              >
                {DUREE_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>

              {/* Boutons Enregistrer / Annuler (visibles si modifié) */}
              {dirty ? (
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => handleSave(t)}
                    disabled={isSaving}
                    className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-3 py-1.5 rounded-lg text-xs font-semibold transition"
                  >
                    {isSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : <CheckCircle className="w-3 h-3" />}
                    Enregistrer
                  </button>
                  <button
                    onClick={() => handleCancel(t)}
                    className="px-2 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100 transition"
                  >
                    <X size={14} />
                  </button>
                </div>
              ) : isSaved ? (
                <span className="flex items-center gap-1 text-xs text-teal-600 font-semibold shrink-0">
                  <CheckCircle className="w-3.5 h-3.5" /> Enregistré
                </span>
              ) : null}
            </div>
          );
        })}
      </div>

      {/* ── Options d'alerte ──────────────────────────────────── */}
      <div className="border border-gray-100 rounded-xl p-4 space-y-4">
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Options d'alerte</p>
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1.5">Alerter avant expiration</label>
          <div className="flex items-center gap-3">
            <input
              type="range" min="7" max="90" step="7"
              value={draft.rgpd_alert_days}
              onChange={e => onChange({ rgpd_alert_days: Number(e.target.value) })}
              className="flex-1 accent-teal-600"
            />
            <span className="text-sm font-bold text-gray-700 w-20 text-right">{draft.rgpd_alert_days} jours</span>
          </div>
        </div>
        <div className="flex items-center justify-between py-1">
          <div>
            <p className="text-sm font-semibold text-gray-800">Suppression automatique</p>
            <p className="text-xs text-gray-400 mt-0.5">Supprimer les documents arrivés à échéance automatiquement.</p>
          </div>
          <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-4">
            <input
              type="checkbox" className="sr-only peer"
              checked={draft.rgpd_auto_delete}
              onChange={() => onChange({ rgpd_auto_delete: !draft.rgpd_auto_delete })}
            />
            <div className="w-9 h-5 bg-gray-200 peer-checked:bg-red-500 rounded-full transition after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4" />
          </label>
        </div>
      </div>
    </div>
  );
}

// ── Utilisateurs ──────────────────────────────────────────────────

const ROLES: Record<string, { label: string; color: string }> = {
  admin:   { label: 'Administrateur', color: 'bg-violet-50 text-violet-700 border-violet-200' },
  manager: { label: 'Business Manager',     color: 'bg-teal-50 text-teal-700 border-teal-200'       },
  viewer:  { label: 'Lecture seule',  color: 'bg-gray-100 text-gray-600 border-gray-200'      },
};

function UsersTab() {
  const [users, setUsers] = useState<User[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<UserCreate>({ full_name: '', email: '', role: 'viewer' });

  const load = () => usersApi.list().then(r => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const handleAdd = async () => {
    if (!form.full_name.trim() || !form.email.trim()) return;
    await usersApi.create(form);
    setForm({ full_name: '', email: '', role: 'viewer' });
    setShowForm(false);
    load();
  };

  const handleRemove = async (id: string) => {
    if (!confirm('Désactiver cet utilisateur ?')) return;
    await usersApi.delete(id);
    load();
  };

  const handleRoleChange = async (id: string, role: string) => {
    await usersApi.update(id, { role: role as User['role'] });
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={20} className="text-gray-400" strokeWidth={1.5} />
          <div>
            <h3 className="font-bold text-gray-900 text-lg">Utilisateurs</h3>
            <p className="text-sm text-gray-500 mt-0.5">Gérez les accès et les rôles des utilisateurs.</p>
          </div>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
        >
          <UserPlus size={15} strokeWidth={2} />
          Inviter
        </button>
      </div>

      {showForm && (
        <div className="border border-teal-200 rounded-xl p-4 bg-white space-y-3 shadow-sm">
          <p className="font-bold text-gray-900 text-sm">Nouvel utilisateur</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <input
              type="text" placeholder="Nom complet"
              value={form.full_name}
              onChange={e => setForm(f => ({ ...f, full_name: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
            />
            <input
              type="email" placeholder="email@organisation.lu"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-teal-500/30 focus:border-teal-400 transition"
            />
            <select value={form.role} onChange={e => setForm(f => ({ ...f, role: e.target.value as User['role'] }))} className="border border-gray-200 rounded-lg px-3 py-2.5 text-sm bg-white">
              {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => setShowForm(false)} className="border border-gray-200 px-3 py-2 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Annuler</button>
            <button onClick={handleAdd} disabled={!form.full_name.trim() || !form.email.trim()} className="bg-teal-600 hover:bg-teal-700 disabled:bg-teal-300 text-white px-4 py-2 rounded-lg text-sm font-semibold transition">Ajouter</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {users.map(u => (
          <div key={u.id} className="flex items-center justify-between px-4 py-3.5 border border-gray-100 rounded-xl bg-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-teal-100 text-teal-700 font-bold text-sm flex items-center justify-center shrink-0">
                {u.full_name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-gray-900">{u.full_name}</p>
                  {u.role === 'admin' && <Crown size={13} className="text-violet-400" strokeWidth={1.8} />}
                </div>
                <p className="text-xs text-gray-400">{u.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={u.role}
                onChange={e => handleRoleChange(u.id, e.target.value)}
                className={`text-xs font-semibold border rounded-full px-2.5 py-1 bg-white ${ROLES[u.role]?.color}`}
              >
                {Object.entries(ROLES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              {users.length > 1 && (
                <button onClick={() => handleRemove(u.id)} className="p-1.5 hover:bg-red-50 text-gray-300 hover:text-red-400 rounded-lg transition">
                  <XCircle size={16} strokeWidth={1.6} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Page principale ───────────────────────────────────────────────

const SECTIONS = [
  { key: 'general',       label: 'Général',             icon: SlidersHorizontal },
  { key: 'types',         label: 'Types de documents',  icon: FileText  },
  { key: 'notifications', label: 'Notifications',       icon: Bell      },
  { key: 'rgpd',          label: 'RGPD & Conservation', icon: Shield    },
  { key: 'users',         label: 'Utilisateurs',        icon: Users     },
  { key: 'integrations',  label: 'Intégrations',        icon: Plug      },
];

const DEFAULT_SETTINGS: SettingsDraft = {
  organisation_name: 'ACO',
  default_language: 'fr',
  timezone: 'Europe/Luxembourg',
  classification_threshold: 80,
  ocr_threshold: 80,
  notification_email: '',
  notify_new_doc: false,
  notify_rgpd_expiry: true,
  notify_scan_error: true,
  notify_low_confidence: true,
  notify_weekly_report: false,
  rgpd_alert_days: 30,
  rgpd_auto_delete: false,
};

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('general');
  const [draft, setDraft] = useState<SettingsDraft>(DEFAULT_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [autoTypeCount, setAutoTypeCount] = useState(0);

  useEffect(() => {
    settingsApi.get()
      .then(r => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { updated_at, ...rest } = r.data;
        setDraft(rest);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    const refresh = () => {
      documentTypesApi.list()
        .then(r => setAutoTypeCount(r.data.filter(t => t.auto_created).length))
        .catch(() => setAutoTypeCount(0));
    };
    refresh();
    window.addEventListener('acodoc:refresh-sidebar', refresh);
    return () => window.removeEventListener('acodoc:refresh-sidebar', refresh);
  }, []);

  const handleChange = (patch: Partial<SettingsDraft>) => {
    setDraft(d => ({ ...d, ...patch }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await settingsApi.update(draft);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { updated_at, ...rest } = r.data;
      setDraft(rest);
      setSaved(true);
      toast('Paramètres enregistrés avec succès');
      setTimeout(() => setSaved(false), 2500);
    } catch {
      toast('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setDraft(DEFAULT_SETTINGS);

  const renderContent = () => {
    switch (activeTab) {
      case 'general':       return <GeneralTab draft={draft} onChange={handleChange} />;
      case 'types':         return <TypesTab />;
      case 'notifications': return <NotificationsTab draft={draft} onChange={handleChange} />;
      case 'rgpd':          return <RgpdTab draft={draft} onChange={handleChange} />;
      case 'users':         return <UsersTab />;
      case 'integrations':  return <IntegrationsTab />;
      default:              return <GeneralTab draft={draft} onChange={handleChange} />;
    }
  };

  return (
    <div className="flex-1 overflow-y-auto">
      <Header title="Paramètres">
        <button onClick={handleReset} className="border border-gray-200 bg-white text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">Annuler</button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-1.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-4 py-2 rounded-lg text-sm font-semibold transition"
        >
          <Save size={15} strokeWidth={2} />
          {saving ? 'Enregistrement…' : saved ? 'Enregistré ✓' : 'Enregistrer'}
        </button>
      </Header>
      <div className="p-4 sm:p-6">
        <div className="flex gap-1.5 overflow-x-auto pb-2 mb-4 lg:hidden">
          {SECTIONS.map((s) => (
            <button
              key={s.key}
              onClick={() => setActiveTab(s.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition shrink-0 ${activeTab === s.key ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
            >
              <s.icon size={13} strokeWidth={1.8} />
              {s.label}
              {s.key === 'types' && autoTypeCount > 0 && (
                <span
                  className="ml-1 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold animate-pulse"
                  title={`${autoTypeCount} type(s) à configurer`}
                >
                  {autoTypeCount > 99 ? '99+' : autoTypeCount}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="flex gap-6">
          <div className="hidden lg:block w-56 bg-white rounded-xl border border-gray-100 shadow-sm p-2 shrink-0 self-start">
            <p className="px-3 py-2 text-xs font-bold text-gray-400 uppercase tracking-wider">Configuration</p>
            {SECTIONS.map((s) => (
              <button
                key={s.key}
                onClick={() => setActiveTab(s.key)}
                className={`w-full inline-flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium transition ${activeTab === s.key ? 'bg-teal-50 text-teal-700 border border-teal-200' : 'text-gray-600 hover:bg-gray-50'}`}
              >
                <s.icon size={16} strokeWidth={1.6} />
                <span className="flex-1 text-left">{s.label}</span>
                {s.key === 'types' && autoTypeCount > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-amber-500 text-white text-[10px] font-bold animate-pulse"
                    title={`${autoTypeCount} type(s) à configurer`}
                  >
                    {autoTypeCount > 99 ? '99+' : autoTypeCount}
                  </span>
                )}
              </button>
            ))}
          </div>
          <div className="flex-1 bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-6 min-w-0">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
